import React, {useEffect, useState} from "react";
import {Alert, Button, Card, Col, Divider, Form, Input, message, Row, Select, Slider, Space, Switch, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {changePassword} from "./profile-api";
import {useSelfProfile} from "../header-query";
import {useUpdateProfile} from "./profile-query";
import {SoundOutlined} from "@ant-design/icons";
import {SOUNDS, getSoundPreferences, previewSound, setSoundPreferences} from "../../../config/notification-sound";
import {isPasswordChangeRequired, setMustChangePassword} from "../../../config/must-change-password";

const Profile = () => {
    const {t, i18n, ready} = useTranslation();
    const [form] = Form.useForm();
    const [pwdForm] = Form.useForm();

    const [pwdLoading, setPwdLoading] = useState(false);

    // Read once on mount so the banner does not blink away mid-render; cleared explicitly on success.
    const [changeRequired, setChangeRequired] = useState(() => isPasswordChangeRequired());

    // Read once on mount; every change is written straight back so the setting survives a reload
    // and applies to the very next notification, without a save button to forget.
    const [sound, setSound] = useState(() => getSoundPreferences());

    const updateSound = (patch) => {
        const next = {...sound, ...patch};
        setSound(next);
        setSoundPreferences(next);
    };

    const isSupervisor = (localStorage.getItem("role") || "") === "SUPERVISOR";

    const {data: me, isLoading: profileLoading, isError: profileError} = useSelfProfile();
    const {mutateAsync: updateProfile, isPending: saving} = useUpdateProfile();

    useEffect(() => {
        if (me) {
            form.setFieldsValue({
                id: me?.id || "",
                fullName: me?.fullName || "",
                email: me?.email || "",
            });
        }
    }, [me, form]);

    // Handle initial load error (optional)
    useEffect(() => {
        if (profileError) {
            const text =
                ready || i18n.hasLoadedNamespace("translation")
                    ? t("profile.load_failed")
                    : "بارگذاری اطلاعات ناموفق بود.";
            message.error(text);
        }
    }, [profileError, ready, i18n, t]);

    const onSaveProfile = async () => {
        try {
            const values = await form.validateFields();
            await updateProfile(values); // React Query mutation
            message.success(t("profile.update_success"));
        } catch (e) {
            if (!e?.errorFields) {
                message.error(t("profile.update_failed"));
            }
        }
    };


    const onChangePassword = async () => {
        try {
            const v = await pwdForm.validateFields();
            setPwdLoading(true);
            await changePassword({oldPassword: v.oldPassword, newPassword: v.newPassword});
            pwdForm.resetFields();
            // Cleared only here — the one path where the password is chosen by the person it
            // belongs to. This is what releases the redirect in the dashboard shell.
            setMustChangePassword(false);
            setChangeRequired(false);
            message.success(t("profile.password_changed"));
        } catch (e) {
            if (!e?.errorFields) {
                // The backend's own reason is the useful one — "at least 8 characters", "cannot be
                // the same as your email", "current password is incorrect". Falling straight to a
                // generic failure hid all three.
                const reason =
                    e?.response?.data?.message ||
                    e?.response?.data?.error ||
                    t("profile.password_change_failed");
                message.error(reason);
            }
        } finally {
            setPwdLoading(false);
        }
    };

    return (
        <div className="profile-wrap">
            {/* Says what is being asked and why, on the screen where it gets done. The dialog in the
                shell explains the redirect once; this stays until the password is actually changed,
                so somebody who dismisses the dialog and gets distracted still knows what is owed. */}
            {changeRequired && (
                <Alert
                    type="warning"
                    showIcon
                    className="profile-card"
                    style={{marginBottom: 12}}
                    message="رمز عبور خود را تغییر دهید"
                    description="رمز فعلی را شخص دیگری برای شما تعیین کرده است. تا وقتی رمز تازه‌ای انتخاب نکنید، هر بار به همین صفحه هدایت می‌شوید. فرم «تغییر رمز عبور» پایین همین صفحه است."
                />
            )}
            <Card
                title={t("profile.title")}
                bodyStyle={{padding: 16}}
                className="profile-card"
                loading={profileLoading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="id" hidden>
                        <Input type="hidden"/>
                    </Form.Item>
                    <Form.Item
                        label={t("profile.email")}
                        name="email"
                    >
                        <Input disabled/>
                    </Form.Item>

                    <Form.Item
                        label={t("profile.first_name")}
                        name="fullName"
                        rules={isSupervisor ? [] : [{required: true, message: t("profile.required")}]}
                        extra={isSupervisor ? t("profile.name_set_by_admin") : undefined}
                    >
                        <Input maxLength={100} disabled={isSupervisor}/>
                    </Form.Item>

                    {/*
                      * No save button for a supervisor: the account is a system_users row with no
                      * operator or admin profile behind it, so there is nothing to save to. Showing
                      * the button anyway meant a button that answered 403 — worse than its absence,
                      * because it reads as a fault in their account. The password form below is
                      * theirs to use, and is the one thing they must be able to reach here.
                      */}
                    {!isSupervisor && (
                        <div className="actions-row">
                            <Button type="primary" onClick={onSaveProfile} loading={saving}>
                                {t("profile.save_changes")}
                            </Button>
                        </div>
                    )}
                </Form>

                <Divider/>

                {/*
                  * The operator's own notification sound.
                  *
                  * Lives here rather than in an admin screen because it is a personal working
                  * condition, not a system setting: an operator sits under this sound for a whole
                  * shift, and until now both the volume and the tone were hardcoded.
                  */}
                <Card
                    size="small"
                    title="صدای اعلان"
                    bodyStyle={{paddingTop: 12}}
                    className="profile-subcard"
                >
                    <Space direction="vertical" size={14} style={{width: "100%"}}>
                        <Space align="center" style={{justifyContent: "space-between", width: "100%"}}>
                            <span>پخش صدا هنگام دریافت پیام</span>
                            <Switch
                                checked={sound.enabled}
                                onChange={(enabled) => updateSound({enabled})}
                            />
                        </Space>

                        <div>
                            <div style={{marginBottom: 4}}>نوع صدا</div>
                            <Select
                                style={{width: "100%"}}
                                value={sound.name}
                                disabled={!sound.enabled}
                                onChange={(name) => {
                                    updateSound({name});
                                    // Play it as they pick, so choosing is by ear rather than by name.
                                    previewSound(name, sound.volume);
                                }}
                                options={SOUNDS.map((s) => ({label: s.label, value: s.id}))}
                            />
                        </div>

                        <div>
                            <div style={{marginBottom: 4}}>شدت صدا</div>
                            <Row align="middle" gutter={12}>
                                <Col flex="auto">
                                    <Slider
                                        min={0}
                                        max={100}
                                        disabled={!sound.enabled}
                                        value={Math.round(sound.volume * 100)}
                                        onChange={(v) => updateSound({volume: v / 100})}
                                        // Preview on release, not on every drag step — otherwise the
                                        // slider machine-guns the sound while being moved.
                                        onChangeComplete={(v) => previewSound(sound.name, v / 100)}
                                        tooltip={{formatter: (v) => `${v}٪`}}
                                    />
                                </Col>
                                <Col>
                                    <Button
                                        icon={<SoundOutlined/>}
                                        disabled={!sound.enabled}
                                        onClick={() => previewSound(sound.name, sound.volume)}
                                    >
                                        پخش نمونه
                                    </Button>
                                </Col>
                            </Row>
                        </div>

                        <Typography.Text type="secondary" style={{fontSize: 12}}>
                            این تنظیم روی همین دستگاه ذخیره می‌شود؛ روی گوشی و لپ‌تاپ می‌توانید صدای متفاوتی داشته باشید.
                        </Typography.Text>
                    </Space>
                </Card>

                <Divider/>

                <Card
                    size="small"
                    title={t("profile.change_password")}
                    bodyStyle={{paddingTop: 12}}
                    className="profile-subcard"
                >
                    <Form form={pwdForm} layout="vertical">
                        <Form.Item
                            label={t("profile.old_password")}
                            name="oldPassword"
                            rules={[{required: true, message: t("profile.required")}]}
                        >
                            <Input.Password/>
                        </Form.Item>

                        <Form.Item
                            label={t("profile.new_password")}
                            name="newPassword"
                            rules={[{required: true, message: t("profile.required")}]}
                        >
                            <Input.Password/>
                        </Form.Item>

                        <div className="actions-row">
                            <Button type="primary" onClick={onChangePassword} loading={pwdLoading}>
                                {t("profile.submit_new_password")}
                            </Button>
                        </div>
                    </Form>
                </Card>
            </Card>

            {/* Responsive helpers */}
            <style>{responsiveCSS}</style>
        </div>
    );
};

export default Profile;

/* ---------- Responsive CSS (layout-only, no functionality change) ---------- */
const responsiveCSS = `
  .profile-wrap {
    display: grid;
    place-items: start center;
    padding: 12px;
  }

  /* No background here on purpose.
   *
   * These two rules used to hardcode #fff and #fafafa, which overrode the themed card colour and
   * left this screen white in dark mode — with light label text on a white card (invisible) and
   * dark-themed inputs on it (black boxes). Card already paints itself with colorBgContainer, so
   * the correct fix is to say nothing and let the theme do its job. */
  .profile-card {
    width: 100%;
    max-width: clamp(320px, 92vw, 840px);
    margin: 0 auto;
  }

  /* The password section is meant to read as recessed. --npe-surface-sunken is the token the rest
   * of the console uses for exactly that, and it follows light/dark. */
  .profile-subcard {
    background: var(--npe-surface-sunken);
    border-color: var(--npe-hairline);
  }

  /* Center primary action buttons on small screens; left-align on larger */
  .actions-row {
    display: flex;
    gap: 8px;
    justify-content: center;
  }
  @media (min-width: 576px) {
    .actions-row {
      justify-content: flex-start;
    }
  }

  /* OTP row: inline on >=576px, stacked on phones */
  .otp-compact {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    width: 100%;
  }
  @media (max-width: 575.98px) {
    .otp-compact {
      grid-template-columns: 1fr;
    }
    .otp-compact .ant-btn {
      width: 100%;
    }
  }

  /* Tighter paddings on very small devices */
  @media (max-width: 360px) {
    .profile-card .ant-card-body {
      padding: 12px !important;
    }
  }
`;
